import {ShiftingType} from "@mytour/shared";
import type {UseFormReturn} from "react-hook-form";
import {Button, ButtonGroup} from "reactstrap";
import z from "zod";
import {RealSetup} from "./components/RealSetup";
import {ZoneSetup} from "./components/ZoneSetup";

export const step3ShiftingPriceSchema = z.object({
  shiftingType: z.enum(["none", "real", "zone"]),
  shiftingAmount: z.number().gt(0, {message: "Obligatoire"}),
  zones: z
    .array(
      z.object({
        id: z.string(),
        label: z.string().min(3, {message: "Obligatoire"}),
        distance: z.number().optional(),
        amount: z.number().min(0, {message: "Obligatoire"}),
      })
    )
    .superRefine((zones, ctx) => {
      let prevDistance: null | number = null;
      for (let index = 0; index < zones.length; index++) {
        const zone = zones[index];
        if (!zone) {
          continue;
        }
        if (prevDistance !== null && zone.distance && prevDistance >= zone.distance) {
          ctx.addIssue({
            code: "custom",
            path: [`[${index}].distance`],
            message: "Distance invalide",
          });
        }
        if (zone.distance) {
          prevDistance = zone.distance;
        }
      }
    }),
});
export type Step3ShiftingPriceForm = z.infer<typeof step3ShiftingPriceSchema>;

type Props = {
  hookForm: UseFormReturn<Step3ShiftingPriceForm>;
  disabled: boolean;
};

export function Step3ShiftingPrice({hookForm, disabled}: Props) {
  const {watch, setValue} = hookForm;

  const shiftingType = watch("shiftingType");

  return (
    <>
      <p style={{marginBottom: "8px"}}>
        <strong>Vos frais de déplacement</strong>
      </p>
      <ButtonGroup>
        <Button
          id="real"
          color="primary"
          onClick={() => handleMode(ShiftingType.REAL)}
          active={shiftingType === ShiftingType.REAL}
        >
          au km
        </Button>
        <Button
          id="zone"
          color="primary"
          onClick={() => handleMode(ShiftingType.ZONE)}
          active={shiftingType === ShiftingType.ZONE}
        >
          par zone
        </Button>
        <Button
          id="none"
          color="primary"
          onClick={() => handleMode(ShiftingType.NONE)}
          active={shiftingType === ShiftingType.NONE}
        >
          sans frais
        </Button>
      </ButtonGroup>
      {shiftingType === ShiftingType.REAL && (
        <>
          <p style={{marginTop: "16px"}}>
            {`Vous avez sélectionné l'option `}
            <strong>{"frais de déplacement au km"}</strong>
            {"."}
          </p>
          <p
            style={{marginTop: "16px"}}
          >{`Les frais de déplacement d'une journée seront partitionnés entre les clients.`}</p>

          <RealSetup hookForm={hookForm} disabled={disabled} />

          <p>
            {"Pour une moyenne de 40000 km/an, "}
            <br />
            <strong>{"0,2 €/km"}</strong>
            {" est un "}
            <strong>{"minimum"}</strong>
            {"."}
          </p>
          <p>
            <i>{"Exemple basé sur un véhicule consommant 6l/100 kms et un carburant à 2€/l."}</i>
          </p>
        </>
      )}
      {shiftingType === ShiftingType.ZONE && (
        <>
          <p style={{marginTop: "16px"}}>
            {`Vous avez sélectionné l'option `}
            <strong>{"frais de déplacement par zone"}</strong>
            {"."}
          </p>
          <p
            style={{marginTop: "16px"}}
          >{`Les frais de déplacement seront calculés suivant la distance entre votre adresse et l'adresse de prestation.`}</p>
          <ZoneSetup hookForm={hookForm} disabled={disabled} />
        </>
      )}
      {shiftingType === ShiftingType.NONE && (
        <>
          <p style={{marginTop: "16px"}}>
            {`Vous avez sélectionné l'option `}
            <strong>{"sans frais de déplacement"}</strong>
            {"."}
          </p>
          <p>{`Entre le carburant, les révisions, les pièces d'usures... Les frais de déplacement sont importants.`}</p>
          <p>{`Nous vous conseillons d'étudier vos dépenses annuelles !`}</p>
        </>
      )}
    </>
  );

  function handleMode(newType: ShiftingType): void {
    setValue("shiftingType", newType as "none" | "real");
    // Reset shiftingAmount to a valid value when leaving REAL mode,
    // otherwise .gt(0) validation blocks the hidden field.
    if (newType !== ShiftingType.REAL) {
      setValue("shiftingAmount", 0.2);
    }
  }
}
